import { FieldFunctionOptions, InMemoryCacheConfig } from '@apollo/client/core';
import { introspection } from '@ats/api/cm';

// https://www.apollographql.com/docs/react/caching/cache-field-behavior#merging-arrays-of-non-normalized-objects
const mergeArrayByField =
	(field: string) =>
	(
		existing = [],
		incoming: any[],
		{ readField, mergeObjects }: FieldFunctionOptions<Record<string, any>, Record<string, any>>
	) => {
		const merged: any[] = existing ? existing.slice(0) : [];
		const keyToIndex: Record<string, number> = Object.create(null);
		if (existing) {
			existing.forEach((item, index) => {
				keyToIndex[readField<string>(field, item)] = index;
			});
		}
		incoming.forEach(item => {
			const name = readField<string>(field, item);
			const index = keyToIndex[name];
			if (typeof index === 'number') {
				merged[index] = mergeObjects(merged[index], item);
			} else {
				keyToIndex[name] = merged.length;
				merged.push(item);
			}
		});
		return merged;
	};

export const APOLLO_CACHE_CONFIG: InMemoryCacheConfig = {
	typePolicies: {
		CandidateAccount: {
			keyFields: ['cmcId'],
			fields: {
				transcriptDetail(_, { args, toReference }) {
					return toReference({
						__typename: 'CandidateTranscript',
						id: args.id,
					});
				},
				settings: {
					merge: false,
				},
				attributes: {
					merge: false,
				},
				certRequirementList: { merge: false },
				benefitList: { merge: false },
				examAccommPackList: {
					merge: false,
				},
			},
		},
		Exam: {
			fields: {
				imageList: {
					merge: false,
				},
				commentList: {
					merge: false,
				},
				sectionList: {
					merge: false,
				},
				itemList: {
					merge: false,
				},
				violationList: {
					merge: false,
				},
			},
		},
		Candidate: {
			keyFields: (storeObject, context) => {
				if (Object.keys(storeObject).includes('rowId')) {
					return ['cmcId', 'rowId'];
				} else {
					return ['cmcId'];
				}
			},
			fields: {
				identificationList: {
					merge: false,
				},
			},
		},
		CandidateAffiliation: {
			keyFields: false,
		},
		CandidateAuxiliary: {
			keyFields: ['cmcId'],
		},
		IsoCountry: {
			keyFields: ['code'],
		},
		IsoCountrySubdivision: {
			keyFields: ['code'],
		},
		ClientRole: {
			fields: {
				permissions: {
					merge: false,
				},
			},
		},
		ClientSponsor: {
			keyFields: [],
			fields: {
				features: {
					merge: false,
				},
				localizationList: {
					merge: false,
				},
			},
		},
		CandidateSettingAssoc: {
			keyFields: ['cmcId', 'settingItemId'],
		},
		CertUsedItem: {
			keyFields: false,
		},
		CertLogicItemResponse: {
			keyFields: false,
		},
		CertCurrentProgressResponse: {
			keyFields: false,
		},
		DuplicateCandidateResponse: {
			keyFields: false,
		},
		EmailContextOptionResponse: {
			keyFields: ['type', 'id'],
		},
		ETransaction: {
			keyFields: ['transactionId'],
		},
		ExamAccomm: {
			keyFields: ['id'],
		},
		ExamCatalog: {
			fields: {
				scoreReportList: {
					merge: false,
				},
				examSectionCatalogList: {
					merge: false,
				},
				examItemCatalogList: {
					merge: false,
				},
			},
		},
		LocalizationResponse: {
			keyFields: ['name', 'type'],
			fields: {
				value: {
					read(value, { readField }) {
						if (value || value?.trim() === '') {
							return value;
						}
						const defaultValue = readField('defaultValue');
						return defaultValue || null;
					},
				},
			},
		},
		PublicLocalization: {
			keyFields: ['name', 'type'],
		},
		TagDomain: {
			fields: {
				tagItemList: {
					merge: false,
				},
			},
		},
		Tools: {
			keyFields: false,
			merge(existing, incoming) {
				return { ...existing, ...incoming };
			},
			fields: {
				duplicateCandidateBadgeList: {
					merge: false,
				},
				duplicateCandidateDemographicsList: {
					merge: false,
				},
				duplicateCandidateEmailAddressList: {
					merge: false,
				},
				duplicateCandidatePhoneNumberList: {
					merge: false,
				},
				stagingExamPendingList: {
					merge: false,
				},
				defaultClientAliasList: {
					merge: false,
				},
				stagingCandidateErrorList: {
					merge: false,
				},
				stagingCertRequirementErrorList: {
					merge: false,
				},
				benefitVoucherCount: {
					merge: false,
				},
			},
		},
		VoucherCountByBatch: {
			keyFields: ['batchId'],
		},
		Query: {
			fields: {
				newsArticleList: {
					merge: false,
				},
				filterList: {
					merge: false,
				},
			},
		},
		NcarbToolsQueries: {
			keyFields: false,
			merge: true,
			fields: {
				fetchDelayList: {
					merge(existing = [], incoming: any[]) {
						return [...existing, ...incoming];
					},
				},
				fetchDelayClientSettings: {
					merge: mergeArrayByField('csetName'),
				},
				jurisdictionList: {
					merge: false,
				},
			},
		},
		ClientSetting: {
			keyFields: ['csetId'],
			merge: true,
		},
		ClientLocalization: {
			keyFields: ['name', 'locType'],
			fields: {
				clientLocalizationList: {
					merge: false,
				},
			},
		},
		FetchDelayListResponse: {
			keyFields: ['litemId'],
			merge: true,
		},
		EtransactionItemCatalog: {
			keyFields: ['externalId'],
		},
		CreqAuditCandidate: {
			keyFields: ['craId'],
		},
		ComptiaCEVoucherBatch: {
			keyFields: ['batchId', 'createdDate', 'totalCount', 'crcatName'],
			merge: false,
		},
		ComptiaFulfillmentVoucherBatch: {
			keyFields: ['batchId', 'createdDate', 'expireDate', 'totalCount'],
		},
	},
	possibleTypes: introspection.possibleTypes,
};
